import { Flex } from '@chakra-ui/layout';
import {
  Box,
  Button,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import { FeatureFlag, TourClasses } from '@revelio/core';
import { plotColors } from '@revelio/replots';

import { PlusMinusIcon } from '../../../../shared/components/PlusMinusIcon';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import TrialUpgradeLink from '../../../trialUser/TrialUpgradeLink';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import { generatePageKey } from '../engine/filterGroupConsistency';
import { useCurrentSkillTaxonomyVersion } from '../hooks/useCurrentSkillTaxonomyVersion';
import { useCurrentRoleTaxonomyVersion } from '../hooks/useLatestTaxonomyVersion';
import { usePrimaryFilterState } from '../hooks/usePrimaryFilterState';
import { useScrollButtons } from '../hooks/useScrollButtons';
import { FilterId, type PrimaryFilterId } from '../types/filters';
import { PrimaryFilterFormRender } from './filterForms/PrimaryFilterFormRender';
import { PrimaryFilterTag } from './PrimaryFilterTag';
import { ScrollButton } from './ScrollButton';

const FILTER_OPTIONS: {
  value: PrimaryFilterId;
  label: string;
}[] = [
  { value: FilterId.COMPANY, label: 'Company & Industry' },
  { value: FilterId.GEOGRAPHY, label: 'Geography' },
  { value: FilterId.ROLE, label: 'Role' },
];

export const PrimaryFilter = ({
  primaryFilter,
}: {
  primaryFilter: PrimaryFilterConfig;
}) => {
  const { config: pageConfig } = usePageConfig();
  const { config, revelioPageId, dispatch } = useTabContentConfig();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const { dispatch: filterDispatch, defaultSelectionOverrides } =
    useFilterContext();

  const items = config.items;
  const areFilterChipsColored =
    !revelioPageId && getAreFilterChipsColored(items);

  const {
    primaryFilter: primaryTreeFilter,
    togglePrimaryFilter,
    removePrimaryFilter,
    reorderPrimarySelections,
  } = usePrimaryFilterState(primaryFilter.id);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } })
  );

  const hasResetRef = useRef(false);
  const previousFilterIdRef = useRef(primaryFilter.id);
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentSkillTaxonomyVersion();

  const resetPrimaryFilter = useCallback(
    (filterId: PrimaryFilterId) => {
      const filterMap = createDefaultFilterMap(
        filterId,
        {
          roleTaxonomyVersion,
          skillTaxonomyVersion,
        },
        defaultSelectionOverrides
      );
      const pageKey = generatePageKey(filterId, revelioPageId ?? pageConfig.id);
      const defaultActiveCount = primaryFilter.selectionDefault ?? 1;

      filterDispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          defaultActiveCount,
        },
      });

      dispatch({ type: 'UPDATE_PRIMARY_FILTER', payload: filterId });
    },
    [
      revelioPageId,
      primaryFilter?.selectionDefault,
      filterDispatch,
      dispatch,
      pageConfig.id,
      roleTaxonomyVersion,
      skillTaxonomyVersion,
      defaultSelectionOverrides,
    ]
  );

  useEffect(() => {
    // Reset the flag when the primary filter changes
    if (previousFilterIdRef.current !== primaryFilter.id) {
      hasResetRef.current = false;
      previousFilterIdRef.current = primaryFilter.id;
    }

    if (
      !hasResetRef.current &&
      primaryTreeFilter &&
      (primaryTreeFilter.primarySelections?.length ?? 0) === 0
    ) {
      hasResetRef.current = true;
      resetPrimaryFilter(primaryFilter.id);
    }
  }, [primaryTreeFilter, primaryFilter.id, resetPrimaryFilter]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (
      !over ||
      active.id === over.id ||
      !primaryTreeFilter?.primarySelections
    ) {
      return;
    }

    const startingIndex = primaryTreeFilter?.primarySelections?.findIndex(
      (filter) => filter.id === active.id
    );
    const endingIndex = primaryTreeFilter?.primarySelections?.findIndex(
      (filter) => filter.id === over.id
    );
    const newFilterOrder = arrayMove(
      primaryTreeFilter?.primarySelections,
      startingIndex,
      endingIndex
    );

    reorderPrimarySelections(newFilterOrder);
  };

  const { isOpen, onOpen, onClose } = useDisclosure();

  const currentFilterOption = FILTER_OPTIONS.find(
    (opt) => opt.value === primaryFilter.id
  );

  const activeCount =
    primaryTreeFilter?.primarySelections?.filter(
      (selection) => selection.isActive
    ).length || 0;

  const limit = primaryFilter?.selectionLimit;

  const [isHovering, setIsHovering] = useState(false);

  const getChipColor = (idx: number, active: boolean) => {
    const primaryFilterTagColors = primaryFilter.tagColors;
    if (
      (!primaryFilterTagColors && !areFilterChipsColored) ||
      !primaryTreeFilter?.primarySelections
    ) {
      return undefined;
    }
    if (!active) {
      return 'gray.400';
    }
    // Only count actives for color assignment
    const activeSelections = primaryTreeFilter.primarySelections.filter(
      (sel) => sel.isActive
    );
    const activeIdx = activeSelections.findIndex(
      (sel) => sel.id === primaryTreeFilter?.primarySelections?.[idx]?.id
    );
    return plotColors[activeIdx % plotColors.length];
  };

  const {
    containerRef: scrollContainerRef,
    showLeft,
    showRight,
    scrollToEdge,
  } = useScrollButtons();

  const toast = useToast();

  const { isSelfSignupUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  // when opening a primary filter, it should focus on search input
  const popoverFocusRef = useRef<HTMLInputElement>(null);

  // quick/dirty workaround to show toast when user changes primary filter & to use TrialUpgradeLink
  const handleSelectionChange = () => {
    if (isOpen && isSelfSignupUser) {
      if (!toast.isActive('primary-entity-popout-toast')) {
        toast({
          id: 'primary-entity-popout-toast',
          title: 'Upgrade Required',
          description: (
            <span>
              To customize benchmarks,{' '}
              <TrialUpgradeLink toastIdClose="primary-entity-popout-toast" />.
            </span>
          ),
          status: 'warning',
          variant: 'subtle',
          position: 'top-right',
          duration: null,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    return () => {
      toast.closeAll();
    };
  }, [toast]);

  const useNewAnalyticsRendering = isNewDashboardLayout;

  return (
    <Flex
      flexDirection="column"
      marginTop="4px"
      className={TourClasses.TOUR_TRACKING_CLASS}
    >
      <Text
        fontSize="9px"
        fontWeight="600"
        style={{
          opacity: isHovering ? '1' : '0',
          transition: 'opacity 200ms',
        }}
        color="gray.600"
      >
        {`${activeCount}/${limit} SELECTED`}
      </Text>
      <Flex
        alignItems="center"
        columnGap="8px"
        onMouseEnter={() => setIsHovering?.(true)}
        onMouseLeave={() => setIsHovering?.(false)}
        data-testid="filter-chips-toggle-column"
      >
        <Box position="relative" flex="1">
          <Flex
            ref={scrollContainerRef}
            alignItems="center"
            overflowX="auto"
            flexWrap="nowrap"
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              {primaryTreeFilter && primaryTreeFilter?.primarySelections && (
                <SortableContext
                  items={primaryTreeFilter?.primarySelections?.map(
                    (filter) => filter.id
                  )}
                  strategy={horizontalListSortingStrategy}
                >
                  {primaryTreeFilter?.primarySelections?.map((filter, idx) => (
                    <PrimaryFilterTag
                      key={filter.id}
                      id={filter.id}
                      label={filter.name}
                      isActive={filter.isActive}
                      color={getChipColor(idx, filter.isActive)}
                      onClick={(event) => {
                        event.stopPropagation();
                        togglePrimaryFilter(filter.id);
                      }}
                      onRemoveChip={(event) => {
                        event.stopPropagation();
                        removePrimaryFilter(filter.id);
                      }}
                      isSelfSignupUser={isSelfSignupUser}
                    />
                  ))}
                </SortableContext>
              )}
            </DndContext>
          </Flex>

          <ScrollButton
            direction="left"
            visible={showLeft}
            onClick={() => scrollToEdge('left')}
          />
          <ScrollButton
            direction="right"
            visible={showRight}
            onClick={() => scrollToEdge('right')}
          />
        </Box>

        <Popover
          placement="bottom-start"
          onClose={onClose}
          isOpen={isOpen}
          onOpen={onOpen}
          initialFocusRef={popoverFocusRef}
        >
          <PopoverTrigger>
            {useNewAnalyticsRendering ? (
              <IconButton
                aria-label="Add entity"
                icon={<IconPlusSmall />}
                variant="link"
                size="sm"
                minW="auto"
                color="navyBlue.500"
                _hover={{ bg: 'gray.200' }}
                _active={{ bg: 'gray.300' }}
                data-testid="primary-filter-add-button"
              />
            ) : (
              <Button
                variant="link"
                size="sm"
                color="#46bef1"
                fontSize="13px"
                leftIcon={<PlusMinusIcon />}
                data-testid="primary-filter-add-button"
              >
                {currentFilterOption?.label || 'Company & Industry'}
              </Button>
            )}
          </PopoverTrigger>
          <PopoverContent
            minWidth="325px"
            padding={3}
            color="navyBlue.500"
            boxShadow="2xl"
            data-testid={`filter-popover-content-${isOpen ? 'open' : 'closed'}`}
            width="fit-content"
          >
            {primaryTreeFilter && (
              <PrimaryFilterFormRender
                close={onClose}
                selectedFilterId={primaryFilter.id}
                primaryFilter={primaryTreeFilter}
                isOpen={isOpen}
                searchInputRef={popoverFocusRef}
                isSelfSignupUser={isSelfSignupUser}
                selectionChange={handleSelectionChange}
                disallowMultiLevelSelect={false}
              />
            )}
          </PopoverContent>
        </Popover>
      </Flex>
    </Flex>
  );
};

const getAreFilterChipsColored = (items: PositionedItem[]) => {
  return items.some((item) => {
    const chartType = item.plot?.chartType;
    return (
      chartType === 'box' ||
      chartType === 'distribution' ||
      chartType === 'line'
    );
  });
};
